import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { launchModule } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AiAssistantCard } from "@/components/AiAssistantCard";
import {
  DEMO_HEALTH, DEMO_ACTIONS, DEMO_ACTIVITY, SHORTCUTS, ECOSYSTEM_FLOWS, SETUP_CHECKLIST,
} from "@/data/dashboardDemo";
import {
  Grid3x3, Crown, Users, Activity, Clock, FileText, FileSignature, Boxes, LifeBuoy,
  TrendingUp, Target, UserPlus, CreditCard, Zap, CheckCircle2, Circle, Network,
  GraduationCap, ShieldCheck, ExternalLink, ArrowRight, ArrowUpRight, AlertTriangle,
  PlayCircle, BookOpen, DollarSign,
} from "lucide-react";

const ICONS = {
  Grid3x3, Crown, Users, Activity, Clock, FileText, FileSignature, Boxes, LifeBuoy,
  TrendingUp, Target, UserPlus, CreditCard, DollarSign,
};
const Ico = ({ name, ...p }) => { const C = ICONS[name] || Circle; return <C {...p} />; };

const PRIORITY = {
  urgente: { fr: "Urgente", en: "Urgent", color: "#dc143c" },
  normale: { fr: "Normale", en: "Normal", color: "#EAB308" },
  faible: { fr: "Faible", en: "Low", color: "#6b7280" },
};

const fade = { hidden: { opacity: 0, y: 14 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }) };

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`rounded-lg border border-white/10 bg-[#0a0a0a] p-6 ${className}`}>{children}</div>;
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/50">{label}</span>
        <Ico name={icon} className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div className="mt-3 text-3xl font-extrabold text-white font-mono">{value}</div>
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState([]);
  const [realActivity, setRealActivity] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/dashboard/modules").then(({ data }) => setModules(data)).catch(() => {});
    api.get("/activity").then(({ data }) => setRealActivity(data.slice(0, 6))).catch(() => {});
  }, []);

  const L = (fr, en) => (lang === "en" ? en : fr);
  const health = DEMO_HEALTH[lang] || DEMO_HEALTH.fr;
  const actions = DEMO_ACTIONS[lang] || DEMO_ACTIONS.fr;
  const shortcuts = SHORTCUTS[lang] || SHORTCUTS.fr;
  const flows = ECOSYSTEM_FLOWS[lang] || ECOSYSTEM_FLOWS.fr;
  const checklist = SETUP_CHECKLIST[lang] || SETUP_CHECKLIST.fr;
  const activity = realActivity.length ? realActivity : (DEMO_ACTIVITY[lang] || DEMO_ACTIVITY.fr);
  const setupDone = checklist.filter((c) => c.done).length;
  const setupPct = Math.round((setupDone / checklist.length) * 100);
  const urgentCount = actions.filter((a) => a.priority === "urgente").length;
  const isDemo = user.plan === "ecosysteme";

  const openModule = async (m) => {
    if (m.key === "support") { navigate("/support"); return; }
    if (m.connected && m.has_access) {
      const res = await launchModule(m.key);
      if (!res.ok) toast.error(res.message || L("Impossible d'ouvrir le module.", "Unable to open the module."));
      return;
    }
    if (m.construction) { toast.info(L("Module bientôt disponible.", "Module coming soon.")); return; }
    if (!m.has_access) { navigate(`/tarification?module=${m.key}`); return; }
    navigate(`/module/${m.key}`);
  };

  const runShortcut = (s) => {
    if (s.route) { navigate(s.route); return; }
    const m = modules.find((x) => x.key === s.module);
    if (!m || m.construction || !m.has_access) { toast.info(L("Module bientôt disponible.", "Module coming soon.")); return; }
    openModule(m);
  };

  return (
    <motion.div className="space-y-8" data-testid="dashboard-home" initial="hidden" animate="show">
      {/* Greeting */}
      <motion.div variants={fade} custom={0}>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-extrabold text-white">
            {t("dash.greeting")}, <span className="text-[#EAB308]">{user.name.split(" ")[0]}</span>
          </h1>
          {isDemo && (
            <span className="rounded-full border border-[#EAB308]/40 bg-[#EAB308]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#EAB308]" data-testid="demo-badge">
              {L("Compte de démonstration", "Demo account")}
            </span>
          )}
        </div>
        <p className="mt-1 text-white/55">{t("dash.state")} {t(`dash.plan.${user.plan}`)}.</p>
      </motion.div>

      {/* Row 1 : synthèse */}
      <motion.div variants={fade} custom={1} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="Grid3x3" label={t("dash.activeModules")} value={`${stats?.active_modules ?? "—"}/${stats?.total_modules ?? 6}`} accent="#EAB308" />
        <StatCard icon="AlertTriangle" label={L("Actions urgentes", "Urgent actions")} value={urgentCount} accent="#dc143c" />
        <StatCard icon="DollarSign" label={L("Revenus estimés (mois)", "Est. revenue (month)")} value={L("48 500 $", "$48,500")} accent="#EAB308" />
        <StatCard icon="Clock" label={L("Heures à approuver", "Hours to approve")} value="386 h" accent="#dc143c" />
      </motion.div>

      {/* Row 2 : santé de l'entreprise */}
      <motion.div variants={fade} custom={2}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#EAB308]" /> {L("Santé de l'entreprise", "Business health")}</h2>
          {isDemo && <span className="text-[11px] text-white/35">{L("Données de démonstration", "Demo data")}</span>}
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {health.map((h) => (
            <div key={h.key} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4" data-testid={`health-${h.key}`}>
              <div className="flex items-center gap-2 text-xs text-white/45"><Ico name={h.icon} className="w-4 h-4" style={{ color: h.accent }} /> {h.label}</div>
              <div className="mt-2 text-2xl font-extrabold text-white font-mono">{h.value}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Row 3 : actions prioritaires + abonnement */}
      <motion.div variants={fade} custom={3} className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" testid="priority-actions">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Zap className="w-4 h-4 text-[#dc143c]" /> {L("Actions prioritaires", "Priority actions")}</h2>
          </div>
          <div className="mt-4 space-y-2.5">
            {actions.map((a) => {
              const pr = PRIORITY[a.priority];
              return (
                <div key={a.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/40 p-3.5" data-testid={`action-${a.id}`}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md" style={{ background: `${pr.color}14`, border: `1px solid ${pr.color}33` }}>
                    <Ico name={a.icon} className="w-5 h-5" style={{ color: pr.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate">{a.label}</div>
                    <div className="text-xs text-white/45">{a.moduleName}</div>
                  </div>
                  <span className="hidden sm:inline rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase" style={{ color: pr.color, background: `${pr.color}12`, border: `1px solid ${pr.color}33` }}>{pr[lang] || pr.fr}</span>
                  <button onClick={() => openModule(modules.find((m) => m.key === a.module) || { key: a.module, construction: true })} className="rounded-md bg-white/5 border border-white/12 px-3 py-1.5 text-xs font-semibold text-white/80 hover:border-[#EAB308]/40 hover:text-white transition-colors" data-testid={`action-open-${a.id}`}>{L("Ouvrir", "Open")}</button>
                </div>
              );
            })}
          </div>
        </Card>

        <Card testid="subscription-summary">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#EAB308]" /> {L("Votre abonnement", "Your subscription")}</h2>
          <div className="mt-4 space-y-2.5 text-sm">
            <Row k={L("Forfait actuel", "Current plan")} v={t(`dash.plan.${user.plan}`)} />
            <Row k={L("Modules actifs", "Active modules")} v={`${stats?.active_modules ?? "—"}/${stats?.total_modules ?? 6}`} />
            <Row k={L("Modules premium", "Premium modules")} v={stats?.premium_modules ?? "—"} />
            <Row k={L("Statut Stripe", "Stripe status")} v={<span className="text-green-400">{isDemo ? L("Démo", "Demo") : L("Actif", "Active")}</span>} />
            {!isDemo && <Row k={L("Prochain paiement", "Next payment")} v="—" />}
          </div>
          {isDemo && (
            <div className="mt-4 rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.06] px-3.5 py-3 text-xs text-[#f3d98a]">
              {L("Compte de démonstration actif — aucun paiement requis.", "Demo account active — no payment required.")}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={() => navigate("/tarification")} className="rounded-md bg-[#EAB308] text-black py-2 text-xs font-semibold hover:opacity-90 transition-opacity" data-testid="manage-subscription">{L("Gérer", "Manage")}</button>
            <button onClick={() => navigate("/app/billing")} className="rounded-md border border-white/12 bg-white/5 text-white/80 py-2 text-xs font-semibold hover:text-white transition-colors" data-testid="view-invoices">{L("Voir factures", "Invoices")}</button>
          </div>
        </Card>
      </motion.div>

      {/* Row 4 : mes modules */}
      <motion.div variants={fade} custom={4}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Grid3x3 className="w-4 h-4 text-[#EAB308]" /> {L("Mes modules", "My modules")}</h2>
          <button onClick={() => navigate("/app/modules")} className="text-sm text-[#EAB308] hover:underline flex items-center gap-1" data-testid="see-all-modules">{t("dash.seeAll")} <ArrowRight className="w-4 h-4" /></button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {modules.map((m) => {
            const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
            const status = m.construction ? L("Bientôt disponible", "Coming soon") : m.has_access ? L("Actif", "Active") : L("Non inclus", "Not included");
            const statusColor = m.construction ? "#f59e0b" : m.has_access ? "#22c55e" : "#6b7280";
            return (
              <div key={m.key} className="flex flex-col rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid={`my-module-${m.key}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md border" style={{ borderColor: `${accent}44`, background: `${accent}12` }}>
                      <Ico name={m.icon === "Sparkles" ? "FileSignature" : m.icon} className="w-5 h-5" style={{ color: accent }} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{m.name}</div>
                      <div className="text-[11px] text-white/45">{t(`dash.plan.${user.plan}`)}</div>
                    </div>
                  </div>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: statusColor, background: `${statusColor}14`, border: `1px solid ${statusColor}33` }}>{status}</span>
                </div>
                <p className="mt-3 text-xs text-white/50 flex-1">{m.tagline}</p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button onClick={() => openModule(m)} disabled={m.construction} className={`rounded-md py-2 text-xs font-semibold transition-colors ${m.construction ? "bg-white/5 text-white/35 border border-white/10 cursor-not-allowed" : "bg-[#EAB308] text-black hover:opacity-90"}`} data-testid={`my-module-open-${m.key}`}>{L("Ouvrir", "Open")}</button>
                  <button onClick={() => navigate(`/tarification?module=${m.key}`)} className="rounded-md border border-white/12 bg-white/5 py-2 text-xs font-semibold text-white/75 hover:text-white transition-colors" data-testid={`my-module-manage-${m.key}`}>{L("Gérer", "Manage")}</button>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Row 5 : assistant IA + raccourcis */}
      <motion.div variants={fade} custom={5} className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2"><AiAssistantCard /></div>
        <Card testid="quick-shortcuts">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Zap className="w-4 h-4 text-[#EAB308]" /> {L("Raccourcis rapides", "Quick shortcuts")}</h2>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {shortcuts.map((s, i) => (
              <button key={s.label} onClick={() => runShortcut(s)} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-left text-xs font-medium text-white/80 hover:border-[#EAB308]/40 hover:text-white transition-colors" data-testid={`shortcut-${i}`}>
                <Ico name={s.icon} className="w-4 h-4 shrink-0 text-[#EAB308]" /> <span className="truncate">{s.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Row 6 : activité + écosystème */}
      <motion.div variants={fade} custom={6} className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" testid="recent-activity">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Activity className="w-4 h-4 text-[#EAB308]" /> {t("dash.recentActivity")}</h2>
          <div className="mt-4 space-y-3">
            {activity.length === 0 && (
              <div className="text-sm text-white/40">{L("Les prochaines actions apparaîtront ici.", "Upcoming activity will appear here.")}</div>
            )}
            {activity.map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-lg border border-white/8 bg-black/30 p-3" data-testid={`activity-${a.id}`}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md" style={{ background: `${a.accent || "#EAB308"}12`, border: `1px solid ${a.accent || "#EAB308"}30` }}>
                  <Ico name={a.icon || "Activity"} className="w-4 h-4" style={{ color: a.accent || "#EAB308" }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white/85 truncate">{a.text || a.action}</div>
                  <div className="text-xs text-white/40">{a.module ? `${a.module} · ` : ""}{a.who ? `${a.who} · ` : ""}{a.when || a.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card testid="ecosystem-connected">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Network className="w-4 h-4 text-[#EAB308]" /> {L("Écosystème connecté", "Connected ecosystem")}</h2>
          <p className="mt-2 text-xs text-white/50">{L("Vos modules travaillent ensemble pour éviter les doubles saisies.", "Your modules work together to avoid double entry.")}</p>
          <div className="mt-4 space-y-2">
            {flows.map((f) => (
              <div key={f} className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs font-mono text-[#EAB308]">{f}</div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Row 7 : configuration + support/formation */}
      <motion.div variants={fade} custom={7} className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" testid="setup-checklist">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#EAB308]" /> {L("Configuration de votre entreprise", "Company setup")}</h2>
            <span className="text-sm font-mono text-[#EAB308]">{setupPct} %</span>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-gradient-to-r from-[#EAB308] to-[#dc143c] transition-all" style={{ width: `${setupPct}%` }} />
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {checklist.map((c) => (
              <div key={c.key} className="flex items-center gap-2.5 rounded-lg border border-white/8 bg-black/30 px-3 py-2.5 text-sm" data-testid={`setup-${c.key}`}>
                {c.done ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <Circle className="w-4 h-4 text-white/30 shrink-0" />}
                <span className={c.done ? "text-white/50 line-through" : "text-white/85"}>{c.label}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card testid="support-training">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#EAB308]" /> {L("Support & formation", "Support & training")}</h2>
          <div className="mt-4 space-y-2">
            <LinkRow icon={PlayCircle} label={L("Guide de démarrage rapide", "Quick start guide")} onClick={() => navigate("/app/support")} />
            <LinkRow icon={BookOpen} label={L("Tutoriels par module", "Module tutorials")} onClick={() => navigate("/app/modules")} />
            <LinkRow icon={LifeBuoy} label={L("Contacter le support", "Contact support")} onClick={() => navigate("/app/support")} />
            <LinkRow icon={ExternalLink} label={L("Réserver une démo", "Book a demo")} onClick={() => navigate("/#demo")} />
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-white/8 pb-2 last:border-0">
      <span className="text-white/50">{k}</span>
      <span className="font-semibold text-white">{v}</span>
    </div>
  );
}

function LinkRow({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2.5 rounded-lg border border-white/8 bg-black/30 px-3 py-2.5 text-left text-sm text-white/80 hover:border-[#EAB308]/40 hover:text-white transition-colors">
      <Icon className="w-4 h-4 shrink-0 text-[#EAB308]" /> <span className="flex-1">{label}</span>
      <ArrowUpRight className="w-4 h-4 text-white/30" />
    </button>
  );
}
